'use client';

import { useBaseApiCall } from '@/api/factory/base/api';
import { useHandleMutationError } from '@/api/utils';
import { QUERY_KEYS } from '@/features/basket/api/query/index';
import useBasketId from '@/features/basket/hooks/use-basket-id';
import type { Basket } from '@/features/basket/types/basket';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAddProductAndCreateCartMutation } from './add-to-cart';

interface ProductDTO {
  productId: number;
  productQuantity: number;
}

export const useAddProductToCartMutation = (
  onSuccess?: () => void,
  openFlyBasket?: () => void,
) => {
  const basketId = useBasketId();
  const addProductToExistingCart = useBaseApiCall<Basket, [ProductDTO]>(
    (api, product) => api.patch(`basket/${basketId}`, product),
  );
  const handleMutationError = useHandleMutationError();
  const createCartAndAddProductToCart = useAddProductAndCreateCartMutation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: ProductDTO) => {
      if (basketId) {
        return addProductToExistingCart(variables);
      } else {
        return createCartAndAddProductToCart.mutateAsync(variables);
      }
    },
    onSuccess: (data, { productQuantity }) => {
      queryClient.setQueryData([QUERY_KEYS.BASKET, data.id], data);
      onSuccess?.();
      if (productQuantity >= 1) {
        openFlyBasket?.();
      }
    },
    onError: async (error, variables) => {
      if (isAxiosError(error)) {
        if (error.response?.status === 404) {
          const data =
            await createCartAndAddProductToCart.mutateAsync(variables);
          queryClient.setQueryData([QUERY_KEYS.BASKET, data.id], data);
          openFlyBasket?.();
          return;
        }
      }

      return handleMutationError(error);
    },
  });
};
